const LEGACY_KEY = "ecia-search-view";
export type SearchView = "by-part" | "by-distributor";

export default function useSearchView() {
    const { gtag } = useGtag();
    const globalConfig = useStateGlobalConfig();
    const { t } = useI18n();
    const { toast } = useToast();
    const { preferences, updatePreferences } = useDisplayPreferences();

    const featureDefault = computed<SearchView>(() =>
        globalConfig.value.Features.RESULTS_BY_PART_DEFAULT ? "by-part" : "by-distributor"
    );

    // defaultView reflects the persisted cookie preference (SSR-safe).
    const defaultView = computed<SearchView>(() => preferences.value.searchView ?? featureDefault.value);

    // currentView tracks the active tab for this page visit.
    // Initialized from the cookie (SSR-safe) so the correct tab is active on first paint.
    // groupResultsByDistributor now uses a seeded deterministic sort, so rendering the
    // distributor panel on SSR is safe and produces the same output on the client.
    const currentView = ref<SearchView>(defaultView.value);

    // TODO: Remove this migration block after ~July 2026
    if (import.meta.client && !preferences.value.searchView) {
        const stored = localStorage.getItem(LEGACY_KEY);
        if (stored === "by-part" || stored === "by-distributor") {
            updatePreferences({ searchView: stored });
            localStorage.removeItem(LEGACY_KEY);
            // Do not set currentView here — that would cause a hydration mismatch.
            // SSR initialized currentView from defaultView, which had no cookie yet and
            // fell back to the feature flag default. Setting currentView to the localStorage
            // value here would diverge from what SSR rendered before hydration completes.
            // onMounted picks up the migrated cookie value via defaultView.
        }
    }

    const isDefaultByPart = computed<boolean>(() => defaultView.value === "by-part");

    const isDefaultByDistributor = computed<boolean>(() => defaultView.value === "by-distributor");

    function setCurrentView(view: SearchView) {
        currentView.value = view;
    }

    function setDefaultView(view: SearchView) {
        updatePreferences({ searchView: view });
        currentView.value = view;
        const labelType = view === "by-distributor" ? "Distributor" : "Part";
        const description = t(`Search.By${labelType}DefaultViewSuccess`);
        gtag("event", "results_view_set", { results_view_name: view });
        toast({ description });
    }

    return { currentView, defaultView, isDefaultByPart, isDefaultByDistributor, setCurrentView, setDefaultView };
}
